interface ModuleStatus {
  id: string;
  label: string;
  status: 'en_attente' | 'en_cours' | 'termine' | 'erreur';
  resultCount?: number;
}

interface Props {
  modules: ModuleStatus[];
  estimatedTimeRemaining?: number; // secondes
  onCancel: () => void;
  onModuleClick?: (moduleId: string) => void;
}

const STATUS_CONFIG = {
  en_attente: {
    label: 'En attente',
    color: 'text-gray-500',
    bg: 'bg-gray-700',
  },
  en_cours: {
    label: 'En cours',
    color: 'text-cyber-cyan',
    bg: 'bg-cyber-cyan/20',
  },
  termine: {
    label: 'Termine',
    color: 'text-green-400',
    bg: 'bg-green-500/20',
  },
  erreur: {
    label: 'Erreur',
    color: 'text-red-400',
    bg: 'bg-red-500/20',
  },
};

export default function InvestigationProgress({
  modules,
  estimatedTimeRemaining,
  onCancel,
  onModuleClick,
}: Props) {
  const completed = modules.filter(
    (m) => m.status === 'termine' || m.status === 'erreur'
  ).length;
  const total = modules.length;
  const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isRunning = modules.some((m) => m.status === 'en_cours');

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="space-y-4">
      {/* Barre de progression globale */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-300">
            Progression : {completed}/{total} modules
          </span>
          {estimatedTimeRemaining !== undefined && isRunning && (
            <span className="text-xs text-gray-500">
              ~{formatTime(estimatedTimeRemaining)} restant
            </span>
          )}
        </div>
        <div className="w-full h-2 bg-surface-tertiary rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyber-cyan to-helios-600 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Liste des modules */}
      <div className="space-y-2">
        {modules.map((mod) => {
          const config = STATUS_CONFIG[mod.status];
          return (
            <div
              key={mod.id}
              className="flex items-center justify-between px-4 py-3 rounded-lg bg-surface-secondary border border-border-subtle"
            >
              <div className="flex items-center gap-3">
                {/* Icone de statut */}
                {mod.status === 'en_cours' ? (
                  <svg
                    className="w-5 h-5 text-cyber-cyan animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : mod.status === 'termine' ? (
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : mod.status === 'erreur' ? (
                  <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-600" />
                )}

                <div>
                  <span className="text-sm font-medium text-gray-200">
                    {mod.label}
                  </span>
                  <span className={`ml-2 text-xs ${config.color}`}>
                    {config.label}
                  </span>
                </div>
              </div>

              {mod.resultCount !== undefined && mod.status === 'termine' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 bg-surface-tertiary px-2 py-1 rounded">
                    {mod.resultCount} resultat{mod.resultCount !== 1 ? 's' : ''}
                  </span>
                  {onModuleClick && (
                    <button
                      onClick={() => onModuleClick(mod.id)}
                      className="px-3 py-1 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors"
                    >
                      Voir
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bouton annuler */}
      {isRunning && (
        <button
          onClick={onCancel}
          className="w-full py-2.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm font-medium transition-colors"
        >
          Annuler l'investigation
        </button>
      )}
    </div>
  );
}
